// stores/dashboardNoticeStore.ts — transient, NON-persisted dashboard notices.
//
// Carries one-off, non-fatal user notices for the modular dashboard (currently
// the localStorage quota-guard eviction warning, §9.2 / §11 risk 6). It lives
// in its OWN store, deliberately outside the persisted dashboardStore, so a
// notice can never itself be written back into localStorage and re-trigger the
// quota path. Pure in-memory: cleared on reload, dismissable by the user.
//
// The quota-guard storage adapter (dashboardStorage.ts) is a non-React module,
// so it pushes notices via the imperative getState() API rather than a hook.

import { create } from 'zustand';

export interface DashboardNotice {
  id: string;
  /** non-fatal warnings are the only level we surface here. */
  level: 'warn';
  message: string;
  /** epoch ms, for ordering / auto-expiry by the UI if desired. */
  at: number;
}

interface DashboardNoticeStore {
  notices: DashboardNotice[];
  /** append a notice; returns its id. */
  push: (level: DashboardNotice['level'], message: string) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

/** Stable empty reference for selectors that read the (often empty) list. */
export const EMPTY_NOTICES: DashboardNotice[] = Object.freeze(
  [],
) as unknown as DashboardNotice[];

export const useDashboardNoticeStore = create<DashboardNoticeStore>((set) => ({
  notices: EMPTY_NOTICES,
  push: (level, message) => {
    const id = `notice-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 6)}`;
    const notice: DashboardNotice = { id, level, message, at: Date.now() };
    set((s) => ({ notices: [...s.notices, notice] }));
    return id;
  },
  dismiss: (id) =>
    set((s) => {
      const next = s.notices.filter((n) => n.id !== id);
      return { notices: next.length ? next : EMPTY_NOTICES };
    }),
  clear: () => set({ notices: EMPTY_NOTICES }),
}));

/** Imperative push for non-React modules (the quota-guard storage adapter). */
export function pushDashboardNotice(
  level: DashboardNotice['level'],
  message: string,
): string {
  return useDashboardNoticeStore.getState().push(level, message);
}
